import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Calculator } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { goEnquire } from "../../lib/scroll";

const fmtINR = (n) => `₹ ${Math.round(n).toLocaleString("en-IN")}`;

const fmtCompact = (n) => {
  if (n >= 10000000) return `₹ ${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 2)} Cr`;
  if (n >= 100000) return `₹ ${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} L`;
  return fmtINR(n);
};

const SliderRow = ({ label, value, display, min, max, step, onChange, testId }) => (
  <div>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">{label}</span>
      <span className="font-heading text-base font-bold text-navy" data-testid={`${testId}-value`}>{display}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testId}
      className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-[#D4AF37] outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
    />
    <div className="mt-1.5 flex justify-between text-[11px] text-gray-400">
      <span>{typeof min === "number" && min >= 100000 ? fmtCompact(min) : min}</span>
      <span>{typeof max === "number" && max >= 100000 ? fmtCompact(max) : max}</span>
    </div>
  </div>
);

export const EMICalculator = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(5000000);
  const [rate, setRate] = useState(9.5);
  const [years, setYears] = useState(20);

  const { emi, totalInterest, totalPayment, principalPct } = useMemo(() => {
    const r = rate / 12 / 100;
    const n = years * 12;
    const factor = Math.pow(1 + r, n);
    const emiVal = (amount * r * factor) / (factor - 1);
    const total = emiVal * n;
    return {
      emi: emiVal,
      totalInterest: total - amount,
      totalPayment: total,
      principalPct: (amount / total) * 100,
    };
  }, [amount, rate, years]);

  return (
    <section id="emi-calculator" className="relative overflow-hidden border-y border-gray-200 bg-fog py-24 md:py-32" data-testid="emi-calculator-section">
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-gold/10 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          overline="EMI Calculator"
          title="Know Your Monthly Instalment, Instantly."
          sub="Estimate your EMI before you enquire — adjust the amount, rate, and tenure to fit your plan."
          testId="emi-heading"
        />

        <div className="mt-14 grid gap-4 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="flex h-full flex-col justify-between gap-10 rounded-2xl border border-gray-200 bg-white p-8 shadow-[0_2px_10px_rgba(16,20,24,0.04)] md:p-10">
              <div className="space-y-9">
                <SliderRow
                  label="Loan Amount"
                  value={amount}
                  display={fmtCompact(amount)}
                  min={500000}
                  max={100000000}
                  step={500000}
                  onChange={setAmount}
                  testId="emi-amount-slider"
                />
                <SliderRow
                  label="Interest Rate (p.a.)"
                  value={rate}
                  display={`${rate.toFixed(1)} %`}
                  min={6}
                  max={24}
                  step={0.1}
                  onChange={setRate}
                  testId="emi-rate-slider"
                />
                <SliderRow
                  label="Tenure (Years)"
                  value={years}
                  display={`${years} yrs`}
                  min={1}
                  max={30}
                  step={1}
                  onChange={setYears}
                  testId="emi-tenure-slider"
                />
              </div>
              <p className="text-xs leading-relaxed text-gray-400">
                Indicative estimate only. Actual EMI, rates, and eligibility depend on the financial institution's assessment and policies.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between rounded-2xl bg-navy-deep p-8 shadow-[0_16px_48px_rgba(27,44,71,0.35)] md:p-10">
              <div>
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/40 bg-gold/10 text-gold">
                  <Calculator size={20} strokeWidth={1.6} />
                </span>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Monthly EMI</p>
                <p className="mt-2 font-heading text-4xl font-bold tracking-tight text-white md:text-5xl" data-testid="emi-monthly-value">
                  {fmtINR(emi)}
                </p>

                <div className="mt-8 space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/60">Principal Amount</span>
                    <span className="font-semibold text-white" data-testid="emi-principal-value">{fmtCompact(amount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/60">Total Interest</span>
                    <span className="font-semibold text-white" data-testid="emi-interest-value">{fmtCompact(totalInterest)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-4 text-sm">
                    <span className="text-white/60">Total Payment</span>
                    <span className="font-bold text-gold" data-testid="emi-total-value">{fmtCompact(totalPayment)}</span>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/10" data-testid="emi-breakup-bar">
                    <span className="bg-gold transition-all duration-500" style={{ width: `${principalPct}%` }} />
                    <span className="bg-orange transition-all duration-500" style={{ width: `${100 - principalPct}%` }} />
                  </div>
                  <div className="mt-2.5 flex justify-between text-[11px] text-white/60">
                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-gold" /> Principal {principalPct.toFixed(0)}%</span>
                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange" /> Interest {(100 - principalPct).toFixed(0)}%</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => goEnquire(navigate, undefined, fmtCompact(amount))}
                data-testid="emi-enquire-btn"
                className="group mt-10 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-bold text-navy-deep transition-transform duration-300 hover:scale-[1.02]"
              >
                Enquire for This Amount
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
